/**
 * Workforce Hub W2 — employee-scoped audit feed for the Audit tab.
 *
 * Read-only. Refetches when profileId changes; otherwise idle. The
 * server returns at most 100 entries already filtered by
 * (company_id, entity_type='employee_profile', entity_id=profileId)
 * so the client doesn't need to re-scope.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, apiRequest } from '../services/apiClient';

export function useEmployeeAudit(profileId) {
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState(profileId ? 'loading' : 'idle');
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  const fetch = useCallback(async () => {
    if (!profileId) {
      setStatus('idle');
      setEntries([]);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    setError('');
    try {
      const data = await apiRequest(`/api/employees/profiles/${profileId}/audit`, {
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setStatus('loaded');
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof ApiError && err.status === 403) {
        setEntries([]);
        setStatus('forbidden');
        return;
      }
      setError(err?.message || 'Unable to load audit trail.');
      setStatus('error');
    }
  }, [profileId]);

  useEffect(() => {
    fetch();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetch]);

  return { entries, status, error, refetch: fetch };
}

export default useEmployeeAudit;
